import { useCallback, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import type { AbstractAgent } from '@ag-ui/client';
import { useAuiState } from '@assistant-ui/react';
import type { AgUiAssistantRuntime } from '@assistant-ui/react-ag-ui';

import type { Offer } from '@makemydeal/dr-platform-types';

import { DEAL_ASSISTANT_FAILURE_MESSAGE } from '../constants';
import { trackInputInteraction, trackWelcomeLinkClick } from '../monitoring/dealAssistantMonitoring';
import type { DealAssistantFailureDetails } from '../monitoring/dealAssistantMonitoring';
import { DealAssistantContext } from './DealAssistantContext';
import type { DealAssistantContextValue, DealAssistantStatus } from './DealAssistantContext';
import { useDealStructureSnapshot } from './useDealStructureSnapshot';
import { useFailedMessageIds } from './useFailedMessageIds';

export type DealAssistantStateBridgeProps = {
    children: ReactNode;
    runtime: AgUiAssistantRuntime;
    agent: AbstractAgent;
    isOpen: boolean;
    offerSnapshot: Offer | undefined;
    failure: DealAssistantFailureDetails | undefined;
    open: () => void;
    close: () => void;
    clearFailure: () => void;
    getDealStructureSnapshotLabel?: () => string | undefined;
};

const deriveStatus = (isRunning: boolean, hasFailure: boolean, hasMessages: boolean): DealAssistantStatus => {
    if (isRunning) {
        return 'loading';
    }
    if (hasFailure) {
        return 'failure';
    }
    return hasMessages ? 'success' : 'idle';
};

export const DealAssistantStateBridge = ({
    children,
    runtime,
    agent,
    isOpen,
    offerSnapshot,
    failure,
    open,
    close,
    clearFailure,
    getDealStructureSnapshotLabel
}: DealAssistantStateBridgeProps) => {
    const isRunning = useAuiState((state) => state.thread.isRunning);
    const hasMessages = useAuiState((state) => state.thread.messages.length > 0);

    const { isAvailable, unavailableReason, dealStructureSnapshot, dealStructureSnapshotLabel, capture, send } =
        useDealStructureSnapshot({
            runtime,
            agent,
            clearFailure,
            getDealStructureSnapshotLabel
        });

    const failedMessageIds = useFailedMessageIds(failure !== undefined);

    const openWithCapture = useCallback(() => {
        capture();
        open();
    }, [capture, open]);

    const sendingRef = useRef(false);

    const sendMessage = useCallback(
        async (text: string) => {
            const trimmed = text.trim();
            if (!trimmed || isRunning || sendingRef.current) {
                return;
            }
            sendingRef.current = true;
            try {
                await send(trimmed);
            } finally {
                sendingRef.current = false;
            }
        },
        [send, isRunning]
    );

    const value = useMemo<DealAssistantContextValue>(
        () => ({
            isOpen,
            offerSnapshot,
            status: deriveStatus(isRunning, failure !== undefined, hasMessages),
            isAvailable,
            unavailableReason,
            dealStructureSnapshot,
            dealStructureSnapshotLabel,
            failureMessage: failure ? DEAL_ASSISTANT_FAILURE_MESSAGE : undefined,
            open: openWithCapture,
            close,
            sendMessage,
            trackInputInteraction,
            trackWelcomeLinkClick,
            failedMessageIds
        }),
        [
            isOpen,
            offerSnapshot,
            isRunning,
            isAvailable,
            unavailableReason,
            dealStructureSnapshot,
            dealStructureSnapshotLabel,
            failure,
            hasMessages,
            failedMessageIds,
            openWithCapture,
            close,
            sendMessage
        ]
    );

    return <DealAssistantContext.Provider value={value}>{children}</DealAssistantContext.Provider>;
};
